/**
 * Persistent outbox for CRM text mutations: notes, deal patches, stage moves.
 * React-free. Flush triggered by online / visibilitychange (same as image outbox).
 */
import { apiUrl } from "@/lib/api-client";
import {
  appendOfflineSyncFailure,
  readApiErrorBody,
} from "@/lib/offline-sync-failure-log";

const DB_NAME = "tpt-crm-sync-outbox";
const DB_VERSION = 1;
const STORE = "ops";

export type CrmOpType = "note" | "deal_patch" | "deal_stage";

export type CrmSyncOp = {
  id: string;
  createdAt: number;
  type: CrmOpType;
  dealId: string;
  payload: Record<string, unknown>;
  /** Number of failed online flush attempts (transient errors). Dead-lettered past the cap. */
  attempts?: number;
};

/** After this many transient (online) failures, give up so the op cannot clog the queue forever. */
const MAX_TRANSIENT_ATTEMPTS = 20;

export type CrmSyncOpListItem = Omit<CrmSyncOp, "payload"> & {
  preview: string;
};

type SuccessDetail = {
  id: string;
  op: CrmSyncOp;
  response: unknown;
};

type FailureDetail = {
  id: string;
  op: CrmSyncOp;
  status: number;
  errorCode?: string;
  message?: string;
};

async function recordCrmFailureDrop(
  op: CrmSyncOp,
  status: number,
  apiError?: { errorCode?: string; message?: string },
): Promise<void> {
  let preview: string = op.type;
  if (op.type === "note") {
    const body = String(op.payload.body ?? "");
    preview = body.length > 60 ? body.slice(0, 60) + "…" : body;
  } else if (op.type === "deal_patch") {
    const keys = Object.keys(op.payload).filter((k) => k !== "clientMutationId");
    preview = keys.join(", ");
  } else if (op.type === "deal_stage") {
    preview = `→ stage ${String(op.payload.stageId ?? "").slice(0, 8)}`;
  }

  await appendOfflineSyncFailure({
    kind: "crm",
    status,
    title: preview,
    errorCode: apiError?.errorCode ?? (status === 0 ? "network_or_timeout" : undefined),
    message: apiError?.message,
    dealId: op.dealId,
  });
}

async function emitCrmFailure(
  id: string,
  op: CrmSyncOp,
  status: number,
  apiError?: { errorCode?: string; message?: string },
): Promise<void> {
  await recordCrmFailureDrop(op, status, apiError);
  failureBridge?.({ id, op, status, errorCode: apiError?.errorCode, message: apiError?.message });
}
type FlushOutcome = "ok" | "halt" | "drop" | "retry";

/**
 * Classify an HTTP status:
 *  - 2xx: ok
 *  - 401/403: halt the queue (auth) — keep the op, stop processing
 *  - 408/429 + 5xx: transient — keep and retry later
 *  - other 4xx: non-retryable (e.g. 400 invalid stage, 404, 409, 422) — drop so it can't
 *    clog the queue forever
 */
function classifyStatus(status: number): FlushOutcome {
  if (status >= 200 && status < 300) return "ok";
  if (status === 401 || status === 403) return "halt";
  if (status === 408 || status === 429) return "retry";
  if (status >= 400 && status < 500) return "drop";
  return "retry";
}

let successBridge: ((detail: SuccessDetail) => void) | null = null;
let failureBridge: ((detail: FailureDetail) => void) | null = null;
let flushPromise: Promise<void> | null = null;
const pendingListeners = new Set<(n: number) => void>();

function notifyPendingListeners(): void {
  void getCrmSyncPendingCount().then((n) => {
    pendingListeners.forEach((fn) => {
      try {
        fn(n);
      } catch {
        /* ignore */
      }
    });
  });
}

export function setCrmSyncOutboxSuccessBridge(handler: ((detail: SuccessDetail) => void) | null): void {
  successBridge = handler;
}

export function setCrmSyncOutboxFailureBridge(handler: ((detail: FailureDetail) => void) | null): void {
  failureBridge = handler;
}

export function subscribeCrmSyncPendingCount(listener: (count: number) => void): () => void {
  pendingListeners.add(listener);
  void getCrmSyncPendingCount().then(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

export async function enqueueCrmSyncOp(
  type: CrmOpType,
  dealId: string,
  payload: Record<string, unknown>,
): Promise<string> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);

  if (type === "deal_patch") {
    const allReq = store.getAll();
    const existing = ((await idbRequest(allReq)) as CrmSyncOp[]).find(
      (op) => op.type === "deal_patch" && op.dealId === dealId,
    );
    if (existing) {
      const merged: CrmSyncOp = {
        ...existing,
        createdAt: Date.now(),
        payload: { ...existing.payload, ...payload },
      };
      await idbRequest(store.put(merged));
      db.close();
      notifyPendingListeners();
      return existing.id;
    }
  }

  const id = crypto.randomUUID();
  const op: CrmSyncOp = { id, createdAt: Date.now(), type, dealId, payload };
  await idbRequest(store.add(op));
  db.close();
  notifyPendingListeners();
  return id;
}

export async function removeCrmSyncOp(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).delete(id));
  db.close();
  notifyPendingListeners();
}

export async function getCrmSyncPendingCount(): Promise<number> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const n = await idbRequest(tx.objectStore(STORE).count());
  db.close();
  return n;
}

export async function listCrmSyncOps(): Promise<CrmSyncOpListItem[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const rows = (await idbRequest(tx.objectStore(STORE).getAll())) as CrmSyncOp[];
  db.close();
  return rows
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((op) => {
      let preview: string = op.type;
      if (op.type === "note") {
        const body = String(op.payload.body ?? "");
        preview = body.length > 60 ? body.slice(0, 60) + "…" : body;
      } else if (op.type === "deal_patch") {
        const keys = Object.keys(op.payload).filter((k) => k !== "clientMutationId");
        preview = keys.join(", ");
      } else if (op.type === "deal_stage") {
        preview = `→ stage ${String(op.payload.stageId ?? "").slice(0, 8)}`;
      }
      return { id: op.id, createdAt: op.createdAt, type: op.type, dealId: op.dealId, preview };
    });
}

function buildRequest(op: CrmSyncOp): { url: string; init: RequestInit } {
  switch (op.type) {
    case "note":
      return {
        url: apiUrl(`/api/crm/deals/${op.dealId}/notes`),
        init: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(op.payload),
        },
      };
    case "deal_patch":
      return {
        url: apiUrl(`/api/crm/deals/${op.dealId}`),
        init: {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(op.payload),
        },
      };
    case "deal_stage":
      return {
        url: apiUrl(`/api/crm/deals/${op.dealId}/stage?_=${Date.now()}`),
        init: {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(op.payload),
        },
      };
    default:
      throw new Error(`Unknown CRM op type: ${op.type}`);
  }
}

async function deleteOp(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).delete(id));
  db.close();
  notifyPendingListeners();
}

/**
 * Increment the transient-failure counter for an op. Returns the new attempt count.
 * Only called for online failures, so a long offline period never burns attempts.
 */
async function bumpAttempts(id: string): Promise<number> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const current = (await idbRequest(store.get(id))) as CrmSyncOp | undefined;
  if (!current) {
    db.close();
    return Number.MAX_SAFE_INTEGER;
  }
  const attempts = (current.attempts ?? 0) + 1;
  await idbRequest(store.put({ ...current, attempts }));
  db.close();
  return attempts;
}

/**
 * Handle a transient failure (5xx / 408 / 429 / network / timeout): bump the attempt
 * counter and dead-letter the op once it has failed too many times, so a permanently
 * broken op (e.g. a backend bug that returns 500 forever) cannot clog the queue.
 */
async function handleTransientFailure(op: CrmSyncOp, status: number): Promise<FlushOutcome> {
  const attempts = await bumpAttempts(op.id);
  if (attempts >= MAX_TRANSIENT_ATTEMPTS) {
    await deleteOp(op.id);
    await emitCrmFailure(op.id, op, status, { errorCode: "max_retries_exceeded" });
    return "drop";
  }
  return "retry";
}

async function processOne(op: CrmSyncOp): Promise<FlushOutcome> {
  const { url, init } = buildRequest(op);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 30_000);
  try {
    const r = await fetch(url, { ...init, signal: controller.signal });
    if (!r.ok) {
      const outcome = classifyStatus(r.status);
      if (outcome === "drop") {
        const apiError = await readApiErrorBody(r);
        await deleteOp(op.id);
        await emitCrmFailure(op.id, op, r.status, apiError);
        return outcome;
      }
      if (outcome === "retry") {
        return handleTransientFailure(op, r.status);
      }
      return outcome; // "halt"
    }
    let response: unknown;
    try {
      response = await r.json();
    } catch {
      response = null;
    }
    await deleteOp(op.id);
    successBridge?.({ id: op.id, op, response });
    return "ok";
  } catch {
    return handleTransientFailure(op, 0);
  } finally {
    window.clearTimeout(timer);
  }
}

async function runFlush(): Promise<void> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const rows = ((await idbRequest(tx.objectStore(STORE).getAll())) as CrmSyncOp[]).sort(
    (a, b) => a.createdAt - b.createdAt,
  );
  db.close();
  for (const op of rows) {
    const outcome = await processOne(op);
    // "ok" / "drop": op is gone, keep processing the rest.
    // "retry" (transient) / "halt" (auth): stop and try again on the next trigger.
    if (outcome === "retry" || outcome === "halt") break;
  }
}

export function flushCrmSyncOutbox(): Promise<void> {
  if (!flushPromise) {
    flushPromise = runFlush().finally(() => {
      flushPromise = null;
    });
  }
  return flushPromise;
}
